/* ============================================================
   Client-side SVG → PNG rasterization.

   Our servers deliberately REJECT SVG uploads (magic-byte sniff,
   PNG/JPEG/WebP/GIF only) because a raw SVG served same-origin is a
   stored-XSS vector. To still let a user bring an SVG logo, we
   rasterize it to a PNG in the browser and upload THAT — the PNG
   passes the existing gate and no SVG ever reaches storage.

   Canvas-taint caveat: an SVG that pulls in a <foreignObject> or a
   cross-origin <image>/font taints the canvas, so toBlob() throws a
   SecurityError. We reject (never return a blank image) so the caller
   can ask the user for a raster file instead. Scripts inside an SVG do
   NOT execute when it is loaded via <img>, so this stays safe.
   ============================================================ */

const MAX_SIZE = 1024; // hard cap on the longest side of the output PNG
const MIN_SIZE = 512; // floor — upscale tiny/vector logos so they stay crisp

export function isSvgFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type === 'image/svg+xml' || type === 'image/svg') return true;
  // Some pickers report an empty type for .svg — fall back to the extension.
  if (type) return false;
  return (file.name || '').toLowerCase().endsWith('.svg');
}

// Rasterize an SVG File/Blob to a PNG File (transparent background preserved).
// Rejects on a tainted canvas or an undecodable SVG — it never returns a
// blank/broken image.
export async function rasterizeSvgToPng(file, { maxSize = MAX_SIZE } = {}) {
  const svgText = await readText(file);
  if (!/<svg[\s>]/i.test(svgText)) throw new Error('Not a valid SVG');

  const blobUrl = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }));
  try {
    const img = await loadImage(blobUrl);
    let w = img.naturalWidth || img.width || 0;
    let h = img.naturalHeight || img.height || 0;
    if (!w || !h) { w = h = MIN_SIZE; } // SVG with no intrinsic size → square

    const longest = Math.max(w, h);
    const target = Math.min(maxSize, Math.max(MIN_SIZE, longest));
    const scale = target / longest;
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(img, 0, 0, cw, ch);

    const blob = await toPngBlob(canvas); // throws on a tainted canvas
    const base = (file.name || 'logo').replace(/\.svg$/i, '') || 'logo';
    return new File([blob], `${base}.png`, { type: 'image/png' });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Unable to render SVG'));
    img.src = src;
  });
}

function toPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    try {
      // Tainted-canvas throws a SecurityError synchronously here in most
      // browsers; some invoke the callback with null — handle both.
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('SVG could not be converted'));
      }, 'image/png');
    } catch (err) {
      reject(err);
    }
  });
}

function readText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(blob);
  });
}
